import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../shared/providers/LanguageProvider';
import { fetchProductSalesAnalytics } from '../../services/merchantService';
import { ShimmerTable } from '../../components/common/Shimmer';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';

const ProductSalesAnalytics = () => {
  const { userProfile, session } = useAuth();
  const { t } = useLanguage();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('sales_count');
  const [sortOrder, setSortOrder] = useState('desc');
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = userProfile?.token || session?.access_token;
      if (!token) {
        setError(t('noAuthenticationTokenAvailable'));
        return;
      }

      const params = {
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        q: searchQuery || undefined,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        sort_by: sortBy,
        sort_order: sortOrder
      };

      const result = await fetchProductSalesAnalytics(token, params);
      setData(result);
    } catch (err) {
      console.error('Error fetching product sales analytics:', err);
      setError(err.message || t('somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, searchQuery, sortBy, sortOrder, currentPage, pageSize]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return `SAR ${parseFloat(amount || 0).toFixed(2)}`;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={`${t('productSalesAnalytics')} 📊`}
        description={t('trackProductPerformance')}
        actions={<Button onClick={fetchData}>{t('refresh')}</Button>}
      />

      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-6 text-destructive">{error}</CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('filters')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label>{t('dateFrom')}</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('dateTo')}</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('searchProduct')}</Label>
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder={t('productNamePlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('sortBy')}</Label>
              <Select
                value={sortBy}
                onValueChange={(value) => {
                  setSortBy(value);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales_count">{t('salesCountLabel')}</SelectItem>
                  <SelectItem value="revenue">{t('revenueLabel')}</SelectItem>
                  <SelectItem value="discount_given">{t('discountGivenLabel')}</SelectItem>
                  <SelectItem value="average_order_value">{t('avgOrderValueLabel')}</SelectItem>
                  <SelectItem value="last_sale_at">{t('lastSaleLabel')}</SelectItem>
                  <SelectItem value="product_name">{t('productName')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('sortOrder')}</Label>
              <Select
                value={sortOrder}
                onValueChange={(value) => {
                  setSortOrder(value);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desc">{t('descending')}</SelectItem>
                  <SelectItem value="asc">{t('ascending')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('pageSize')}</Label>
              <Select
                value={String(pageSize)}
                onValueChange={(value) => {
                  setPageSize(parseInt(value));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="200">200</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {data?.summary && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-5">
          <Card>
            <CardContent className="p-6">
              <p className="text-sm font-medium text-muted-foreground">{t('productsTracked')}</p>
              <p className="text-2xl font-bold text-foreground">{data.summary.products_tracked}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <p className="text-sm font-medium text-muted-foreground">{t('totalSales')}</p>
              <p className="text-2xl font-bold text-foreground">{data.summary.total_sales_count}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <p className="text-sm font-medium text-muted-foreground">{t('totalRevenue')}</p>
              <p className="text-2xl font-bold text-foreground">{formatCurrency(data.summary.total_revenue)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <p className="text-sm font-medium text-muted-foreground">{t('totalDiscounts')}</p>
              <p className="text-2xl font-bold text-foreground">{formatCurrency(data.summary.total_discount_given)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <p className="text-sm font-medium text-muted-foreground">{t('avgOrderValueOverall')}</p>
              <p className="text-2xl font-bold text-foreground">{formatCurrency(data.summary.average_order_value_overall)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('productPerformance')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              <ShimmerTable rows={8} columns={7} />
            </div>
          ) : !data?.rows || data.rows.length === 0 ? (
            <div className="py-12 text-center">
              <div className="mb-4 text-6xl">📊</div>
              <h3 className="mb-2 text-xl font-semibold text-foreground">{t('noSalesDataFound')}</h3>
              <p className="text-muted-foreground">{t('tryAdjustingFilters')}</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('productName')}</TableHead>
                    <TableHead>{t('salesCountLabel')}</TableHead>
                    <TableHead>{t('revenueLabel')}</TableHead>
                    <TableHead>{t('discountGivenLabel')}</TableHead>
                    <TableHead>{t('avgOrderValueLabel')}</TableHead>
                    <TableHead>{t('lastSaleLabel')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.rows.map((product, index) => (
                    <TableRow key={product.product_id || index}>
                      <TableCell className="font-medium">{product.product_name || '-'}</TableCell>
                      <TableCell>{product.sales_count || 0}</TableCell>
                      <TableCell className="font-semibold text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(product.revenue)}
                      </TableCell>
                      <TableCell className="text-amber-600 dark:text-amber-400">
                        {formatCurrency(product.discount_given)}
                      </TableCell>
                      <TableCell>{formatCurrency(product.average_order_value)}</TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(product.last_sale_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {data.pagination && data.pagination.total > data.pagination.limit && (
                <>
                  <Separator className="my-4" />
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      {t('showing')} {data.pagination.offset + 1} {t('to')}{' '}
                      {Math.min(data.pagination.offset + data.pagination.limit, data.pagination.total)}{' '}
                      {t('of')} {data.pagination.total} {t('products')}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                      >
                        {t('previous')}
                      </Button>
                      <span className="px-3 py-2 text-sm text-foreground">
                        {t('page')} {currentPage} {t('of')}{' '}
                        {Math.ceil(data.pagination.total / data.pagination.limit)}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={!data.pagination.has_more}
                      >
                        {t('next')}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductSalesAnalytics;
